import { displayPrice } from '../../util/configHelpers';
import { formatMoney } from '../../util/currency';
import { richText } from '../../util/richText';
import {
  listingCardDisplayName,
  listingCardLowestPrice,
  listingCardOneLiner,
} from '../../util/listingCardCopy';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTooltip: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTooltip: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

/**
 * Returns all translated and formatted strings for ListingCard so the
 * presentational component can stay simple and aria-labels use the same copy.
 *
 * @param {Object} listing - API entity: listing or ownListing
 * @param {Object} config - app configuration (e.g. from useConfiguration())
 * @param {Object} intl - React Intl instance (e.g. from useIntl())
 * @returns {Object} translations and derived values
 */
export const getListingCardTranslations = (listing, config, intl) => {
  const { title = '', publicData } = listing?.attributes || {};

  const authorDisplayName = listingCardDisplayName(listing);
  const authorName = intl.formatMessage(
    { id: 'ListingCard.author' },
    { authorName: authorDisplayName }
  );
  const oneLiner = listingCardOneLiner(listing);
  const oneLinerFormatted = oneLiner
    ? richText(oneLiner, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
        longWordClass: css.longWord,
      })
    : '';

  const validListingTypes = config.listing.listingTypes || [];
  const { listingType } = publicData || {};
  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const showPrice = displayPrice(listingTypeConfig);
  const lowestPrice = listingCardLowestPrice(listing);
  const { formattedPrice, priceTooltip } = priceData(lowestPrice, config.currency, intl);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const priceMessage =
    showPrice && lowestPrice?.amount === 0
      ? 'Free text chats available'
      : showPrice && formattedPrice != null
      ? intl.formatMessage(
          { id: 'ListingCard.priceStartingFrom' },
          { priceValue, pricePerUnit: '' }
        )
      : '';

  const cardAriaLabel =
    priceMessage.length > 0
      ? intl.formatMessage(
          { id: 'ListingCard.screenreader.label' },
          { listingTitle: authorDisplayName || title, formattedPrice: priceMessage }
        )
      : authorDisplayName || title;

  return {
    titlePlain: authorDisplayName || title,
    titleFormatted: oneLinerFormatted,
    oneLiner,
    oneLinerFormatted,
    authorName,
    authorDisplayName,
    showPrice,
    priceTooltip,
    priceMessage,
    cardAriaLabel,
  };
};
